import { useContext, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AddWebhookModal from '@/sections/@dashboard/webhooks/AddWebhookModal';
import AppSendTestEventModal from '@/sections/@dashboard/apps/AppSendTestEventModal';
import ConfirmDialog from '@/components/confirm-dialog/ConfirmDialog';
import Iconify from '@/components/iconify';
import WebhookActivityProvider, {
  WebhookActivityContext,
  WebhookActivityScopes,
} from '@/contexts/WebhooksActivitiesContext';
import WebhooksProvider, { WebhooksContext } from '@/contexts/WebhooksContext';
import { PATH_DASHBOARD } from '@/routes/paths';
import { FormScopes } from '@/utils/form';
import { PageTitle } from '@/utils/pageTitle';
import { resolveWebhookPlatform } from '@/utils/webhooks';
import MyWebhooksSection from './Apps/MyWebhooksSection';
import RecentActivitiesSection from './Apps/RecentActivitiesSection';
import WebhookMetricCard from './Apps/WebhookMetricCard';

export function WebhooksContent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isAddWebhookModalOpen, setIsAddWebhookModalOpen] = useState(false);
  const [testEventScope, setTestEventScope] = useState(FormScopes.INDEX);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [selectedWebhookPlatform, setSelectedWebhookPlatform] = useState(null);
  const [webhookToDelete, setWebhookToDelete] = useState(null);

  const {
    metrics,
    webhooks,
    webhookPlatforms,
    webhookPlatform,
    createWebhook,
    deleteWebhook,
    deletingWebhookId,
    isFetchingMetrics,
    isFetchingPlatforms,
    isFetchingWebhooks,
    refetchMetrics,
    refetchWebhooks,
  } = useContext(WebhooksContext);
  const { previewActivities, fetching: isFetchingActivities, refetchActivities } =
    useContext(WebhookActivityContext);

  const handleOpenAddWebhookModal = () => {
    if (webhookPlatform) {
      setIsAddWebhookModalOpen(true);
    }
  };

  const handleCloseAddWebhookModal = () => {
    setIsAddWebhookModalOpen(false);
  };

  const handleOpenSettingsPage = (webhook) => {
    navigate(PATH_DASHBOARD.general.webhookSettings(webhook.id), {
      state: { webhook },
    });
  };

  const handleOpenLogs = (webhook) => {
    navigate(PATH_DASHBOARD.general.webhookLogs(webhook.id), {
      state: { webhook },
    });
  };

  const handleCreateWebhook = async (data) => {
    await createWebhook(data);
    refetchMetrics();
    refetchWebhooks();
    refetchActivities();
  };

  const handleCloseDeleteDialog = () => {
    if (!deletingWebhookId) {
      setWebhookToDelete(null);
    }
  };

  const handleConfirmDeleteWebhook = async () => {
    try {
      await deleteWebhook({ appId: webhookToDelete.id });
    } catch {
      // O hook já avisou do erro e recarregou a lista; o diálogo fica aberto para tentar de novo.
      return;
    }

    setWebhookToDelete(null);
    refetchMetrics();
    refetchWebhooks();
    refetchActivities();
  };

  const handleOpenTestWebhookModal = (webhook) => {
    const webhookData = webhook?.raw || webhook;
    const resolvedPlatform = resolveWebhookPlatform(webhookData, webhookPlatforms, webhookPlatform);

    if (!resolvedPlatform || !webhook?.id) {
      return;
    }

    setSelectedWebhookPlatform(resolvedPlatform);
    setSelectedWebhook(webhookData);
    setTestEventScope(FormScopes.SEND_TEST_EVENT);
  };

  // Só o id: o modal guarda o webhook cru, e a página de logs lê do `state` o webhook já mapeado
  // (com `raw`). Sem `state`, ela resolve o webhook pelo contexto, como num acesso direto à URL.
  const handleOpenTestEventLogs = () => {
    navigate(PATH_DASHBOARD.general.webhookLogs(selectedWebhook.id));
  };

  return (
    <>
      <PageTitle area="Dashboard" screen="Webhooks" />

      <AddWebhookModal
        open={isAddWebhookModalOpen}
        onClose={handleCloseAddWebhookModal}
        onSubmit={handleCreateWebhook}
        platform={webhookPlatform}
      />

      <AppSendTestEventModal
        scope={testEventScope}
        setScope={setTestEventScope}
        platform={selectedWebhookPlatform || webhookPlatform}
        selectedApp={selectedWebhook}
        onViewLogs={handleOpenTestEventLogs}
      />

      <ConfirmDialog
        variant="defaultDanger"
        open={!!webhookToDelete}
        onClose={handleCloseDeleteDialog}
        title="Excluir webhook"
        content={
          webhookToDelete &&
          `Excluir o webhook ${webhookToDelete.name} (${webhookToDelete.url})? Ele deixa de receber eventos novos e sai da sua lista. Se precisar recuperar, fale com o suporte.`
        }
        contentSx={{ maxWidth: 360, overflowWrap: 'anywhere' }}
        action={
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteWebhook}
            loading={!!webhookToDelete && deletingWebhookId === webhookToDelete.id}
          >
            Excluir
          </LoadingButton>
        }
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={4}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography variant="h4">Webhooks</Typography>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.background.paper,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Iconify
                  icon="eva:clock-outline"
                  width={16}
                  height={16}
                  sx={{ color: theme.palette.text.primary }}
                />
                <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                  Dados das últimas 24 horas
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Configure as integrações com os seus apps
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            {metrics.map((metric) => (
              <WebhookMetricCard
                key={metric.key}
                metric={metric}
                isLoading={isFetchingMetrics}
              />
            ))}
          </Box>

          <MyWebhooksSection
            webhooks={webhooks}
            isLoading={isFetchingPlatforms || isFetchingWebhooks}
            onCreateWebhook={handleOpenAddWebhookModal}
            onViewLogs={handleOpenLogs}
            onTestWebhook={handleOpenTestWebhookModal}
            onOpenSettings={handleOpenSettingsPage}
            onDeleteWebhook={setWebhookToDelete}
            deletingWebhookId={deletingWebhookId}
          />

          <RecentActivitiesSection
            activities={previewActivities}
            isLoading={isFetchingWebhooks || isFetchingActivities}
            onViewAll={() => navigate(PATH_DASHBOARD.general.webhooksActivities)}
          />
        </Stack>
      </Container>
    </>
  );
}

export default function Webhooks() {
  return (
    <WebhooksProvider>
      <WebhookActivityProvider scope={WebhookActivityScopes.PREVIEW}>
        <WebhooksContent />
      </WebhookActivityProvider>
    </WebhooksProvider>
  );
}
